import {
  MENU_SECTIONS,
  MEAL_TYPE_ORDER,
  ADDON_UNCATEGORIZED_LABEL,
  FAMILY_PLATTER,
  FAMILY_PLATTER_SERVINGS,
} from 'constants/menuContent';

const scaleUpgradePrices = (options = []) => options.map((o) => ({
  ...o,
  priceALaCarte: (Number(o.priceALaCarte) || 0) * FAMILY_PLATTER_SERVINGS,
  priceSubscription: (Number(o.priceSubscription) || 0) * FAMILY_PLATTER_SERVINGS,
}));

/** Adds a Family Platter variant for every Lunch dish: the Lunch record at 4x its
 *  price (premium-protein upcharges scaled too, since it's 4 servings). */
export function withFamilyPlatters(dishes) {
  const platters = dishes
    .filter((d) => d.mealType === 'Lunch')
    .map((d) => ({
      ...d,
      mealType: FAMILY_PLATTER,
      price: (Number(d.price) || 0) * FAMILY_PLATTER_SERVINGS,
      priceFrom: (Number(d.priceFrom ?? d.price) || 0) * FAMILY_PLATTER_SERVINGS,
      proteinOptions: scaleUpgradePrices(d.proteinOptions),
    }));
  return [...dishes, ...platters];
}

/** Every meal type a dish is on the menu as, keyed by dishId. A dish is one
 *  Weekly Menu record per meal type, and the same dish can belong to more than one
 *  section (a breakfast bowl also sold as a lunch/dinner main), so its variants are
 *  collected menu-wide; each card then offers only its own section's portions. */
function variantsByDishId(dishes) {
  const map = new Map();
  dishes.forEach((dish) => {
    if (!MEAL_TYPE_ORDER.includes(dish.mealType)) return;
    const key = String(dish.dishId);
    if (!map.has(key)) map.set(key, {});
    map.get(key)[dish.mealType] = dish;
  });
  return map;
}

/** Collapse the same dish appearing under several of a section's meal types into
 *  ONE entry (Lunch & Dinner), and give every entry the portions the dish is
 *  its section's portions. The cheapest of the section's OWN variants stays the
 *  default, so a Lunch card opens on Lunch and shows "From $X". */
function collapseVariants(items, sectionMealTypes, allVariants) {
  const byDish = new Map();
  items.forEach((dish) => {
    const key = String(dish.dishId);
    if (!byDish.has(key)) byDish.set(key, []);
    byDish.get(key).push(dish);
  });

  return [...byDish.values()].map((variants) => {
    const ordered = sectionMealTypes
      .map((mt) => variants.find((v) => v.mealType === mt))
      .filter(Boolean);
    const list = ordered.length ? ordered : variants;
    const cheapest = [...list].sort(
      (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
    )[0];
    const variantsByMealType = allVariants.get(String(cheapest.dishId)) || {};
    // Only the portions this section sells: Lunch offers Lunch + Family Platter, Dinner just Dinner.
    const mealTypeOptions = MEAL_TYPE_ORDER.filter((mt) => sectionMealTypes.includes(mt) && variantsByMealType[mt]);
    // One portion is no choice at all — leave those entries as plain dishes so
    // cards and the modal keep their single-price rendering.
    if (mealTypeOptions.length < 2) return { ...cheapest };
    return { ...cheapest, mealTypeOptions, variantsByMealType };
  });
}

/** The portion picker's options for a dish entry: [{ mealType, price }] in
 *  MEAL_TYPE_ORDER, or [] when the dish only comes one way. */
export function mealTypeChoices(dish) {
  const variants = dish?.variantsByMealType;
  if (!variants) return [];
  const mealTypes = dish.mealTypeOptions || Object.keys(variants);
  if (mealTypes.length < 2) return [];
  return mealTypes.map((mealType) => ({
    mealType,
    price: Number(variants[mealType]?.price) || 0,
  }));
}

const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Add-ons grouped as [{ id, label, items }]; an add-on in several categories is
 *  in each group. Ordered by rank (position among the Airtable choices), then
 *  name; uncategorized last. */
export function groupAddOnsByCategory(items, getCategories = (item) => item.addOnCategories) {
  const byCategory = new Map();
  const add = (name, rank, item) => {
    if (!byCategory.has(name)) byCategory.set(name, { items: [], rank: Infinity });
    const group = byCategory.get(name);
    group.items.push(item);
    if (rank != null) group.rank = Math.min(group.rank, rank);
  };
  items.forEach((item) => {
    const categories = (getCategories(item) || []).filter((c) => c?.name?.trim());
    if (!categories.length) add(null, null, item);
    categories.forEach((c) => add(c.name.trim(), c.rank, item));
  });
  const groups = [...byCategory.entries()]
    .filter(([name]) => name)
    .sort(([a, ga], [b, gb]) => ga.rank - gb.rank || a.localeCompare(b))
    .map(([name, g]) => ({ id: slug(name), label: name, items: g.items }));
  if (byCategory.has(null)) {
    groups.push({ id: 'uncategorized', label: ADDON_UNCATEGORIZED_LABEL, items: byCategory.get(null).items });
  }
  return groups;
}

/** Group dishes into Squarespace-style sections (Breakfast, Lunch & Dinner, …). */
export function groupDishesBySection(dishes) {
  const allVariants = variantsByDishId(dishes);
  const used = new Set();
  const sections = [];

  MENU_SECTIONS.forEach((section) => {
    const items = dishes.filter((d) => section.mealTypes.includes(d.mealType));
    items.forEach((d) => used.add(`${d.dishId}-${d.mealType}`));
    if (items.length) {
      sections.push({
        ...section,
        dishes: collapseVariants(items, section.mealTypes, allVariants),
      });
    }
  });

  const other = dishes.filter((d) => !used.has(`${d.dishId}-${d.mealType}`));
  if (other.length) {
    sections.push({ id: 'other', label: 'Additional', mealTypes: [], dishes: other });
  }

  return sections;
}

export function formatMenuWeekTitle(deliveryWeek) {
  if (!deliveryWeek) return "This week's menu";
  try {
    const start = new Date(`${deliveryWeek}T12:00:00`);
    if (Number.isNaN(start.getTime())) return "This week's menu";
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
    const startLabel = `${startMonth} ${start.getDate()}`;
    const endLabel = startMonth === endMonth ? `${end.getDate()}` : `${endMonth} ${end.getDate()}`;

    return `Week of ${startLabel} - ${endLabel} Menu`;
  } catch {
    return "This week's menu";
  }
}

export function ingredientsList(dish) {
  if (dish.ingredients?.length) return dish.ingredients;
  return dish.keyIngredients || [];
}
